import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Reply, Trash2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommentsSection({ storyId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (storyId) loadComments();
  }, [storyId]);

  const loadComments = async () => {
    try {
      const res = await axios.get(`${API}/stories/${storyId}/comments`);
      setComments(res.data.comments);
    } catch (err) {
      console.error("[comments] load failed:", err?.response?.status, err?.message);
      setComments([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;
    setLoading(true);
    try {
      await axios.post(`${API}/stories/${storyId}/comments`, { content: newComment.trim() });
      setNewComment("");
      loadComments();
    } catch (err) {
      console.error("[comments] post failed:", err?.response?.data?.detail || err?.message);
    }
    setLoading(false);
  };

  const handleReply = async (parentId) => {
    if (!replyText.trim() || !user) return;
    setLoading(true);
    try {
      await axios.post(`${API}/stories/${storyId}/comments`, { content: replyText.trim(), parent_id: parentId });
      setReplyText("");
      setReplyingTo(null);
      loadComments();
    } catch (err) {
      console.error("[comments] reply failed:", err?.response?.data?.detail || err?.message);
    }
    setLoading(false);
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      loadComments();
    } catch (err) {
      console.error("[comments] delete failed:", err?.response?.data?.detail || err?.message);
    }
  };

  return (
    <div className="border-t border-[#E5E2D9] mt-4 pt-4" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle size={16} className="text-[#718096]" />
        <span className="font-mono text-xs uppercase tracking-widest text-[#718096]">
          Comments ({comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)})
        </span>
      </div>

      {/* Comment input */}
      {user ? (
        <form onSubmit={handleSubmit} className="flex gap-2 mb-4" data-testid="comment-form">
          <input
            type="text"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            className="flex-1 px-3 py-2 border border-[#E5E2D9] bg-white text-sm focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
            placeholder="Add a comment..."
            data-testid="comment-input"
          />
          <button
            type="submit"
            className="btn-primary text-xs px-3"
            disabled={loading || !newComment.trim()}
            data-testid="submit-comment-btn"
          >
            <Send size={12} />
          </button>
        </form>
      ) : (
        <p className="text-xs text-[#718096] mb-4">Sign in to comment.</p>
      )}

      {/* Comments list */}
      <div className="space-y-3" data-testid="comments-list">
        {comments.map(comment => (
          <div key={comment.id} className="border-l-2 border-[#E5E2D9] pl-3" data-testid={`comment-${comment.id}`}>
            <div className="flex items-baseline justify-between">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-[#1A1A1A]">{comment.author_name}</span>
                <span className="font-mono text-[10px] text-[#718096]">
                  {new Date(comment.timestamp).toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {user && (
                  <button
                    className="btn-ghost p-1 text-[10px]"
                    onClick={() => { setReplyingTo(replyingTo === comment.id ? null : comment.id); setReplyText(""); }}
                    data-testid={`reply-btn-${comment.id}`}
                  >
                    <Reply size={11} />
                  </button>
                )}
                {user && comment.author_id === user.id && (
                  <button
                    className="btn-ghost p-1 text-[10px] text-red-400 hover:text-red-600"
                    onClick={() => handleDelete(comment.id)}
                    data-testid={`delete-comment-${comment.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-[#4A5568] mt-1">{comment.content}</p>

            {/* Reply input */}
            {replyingTo === comment.id && (
              <div className="flex gap-2 mt-2 ml-2" data-testid={`reply-form-${comment.id}`}>
                <input
                  type="text"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-[#E5E2D9] bg-white text-xs focus:ring-2 focus:ring-[#1A2B3C] focus:border-transparent outline-none"
                  placeholder="Reply..."
                  autoFocus
                  data-testid={`reply-input-${comment.id}`}
                />
                <button
                  className="btn-primary text-[10px] px-2 py-1"
                  onClick={() => handleReply(comment.id)}
                  disabled={!replyText.trim()}
                  data-testid={`submit-reply-${comment.id}`}
                >
                  <Send size={10} />
                </button>
              </div>
            )}

            {/* Replies */}
            {comment.replies && comment.replies.length > 0 && (
              <div className="mt-2 ml-4 space-y-2">
                {comment.replies.map(reply => (
                  <div key={reply.id} className="border-l border-[#E5E2D9] pl-3" data-testid={`reply-${reply.id}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-medium text-[#1A1A1A]">{reply.author_name}</span>
                      <span className="font-mono text-[9px] text-[#718096]">
                        {new Date(reply.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-[#4A5568] mt-0.5">{reply.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
